import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatIDR } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowRight, Package as PackageIcon } from "@phosphor-icons/react";

export function Shop() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const r = await api.get("/products");
      setProducts(r.data);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-24">
      <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
        <div className="lg:col-span-8">
          <p className="label-eyebrow text-[hsl(var(--sk-primary))]">{t("shop.eyebrow")}</p>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl tracking-tighter">{t("shop.title")}</h1>
          <p className="mt-6 text-lg text-[hsl(var(--sk-muted-fg))] max-w-xl">{t("shop.subtitle")}</p>
        </div>
      </div>
      {loading ? <p>Loading…</p> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10" data-testid="shop-grid">
          {products.map((p) => (
            <Link to={`/shop/${p.id}`} key={p.id} data-testid={`shop-item-${p.id}`} className="group">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[hsl(var(--sk-muted))] mb-5 relative">
                {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                <div className="absolute top-4 left-4">
                  <Badge className="bg-white text-[hsl(var(--sk-fg))] hover:bg-white uppercase tracking-widest text-[10px]">{p.roast_profile}</Badge>
                </div>
              </div>
              <p className="label-eyebrow text-[hsl(var(--sk-muted-fg))]">{p.origin}</p>
              <h3 className="mt-2 font-display text-2xl tracking-tight">{p.name}</h3>
              <div className="mt-3 flex items-center justify-between">
                <p className="font-mono-stat text-sm">{formatIDR(p.price)}</p>
                <span className="label-eyebrow text-[hsl(var(--sk-primary))] flex items-center gap-1">{t("shop.view")} <ArrowRight size={12} /></span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const { t, lang } = useI18n();
  const { add } = useCart();
  const nav = useNavigate();

  useEffect(() => { api.get(`/products/${id}`).then((r) => setP(r.data)); }, [id]);
  if (!p) return <div className="p-20 text-center">Loading…</div>;

  const desc = lang === "id" ? p.description_id : p.description_en;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid lg:grid-cols-2 gap-16">
      <div className="aspect-square rounded-2xl overflow-hidden bg-[hsl(var(--sk-muted))]">
        {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
      </div>
      <div>
        <p className="label-eyebrow text-[hsl(var(--sk-primary))]">{p.origin}</p>
        <h1 className="mt-3 font-display text-5xl sm:text-6xl tracking-tighter">{p.name}</h1>
        <p className="mt-6 text-lg text-[hsl(var(--sk-muted-fg))] leading-relaxed">{desc}</p>
        <div className="mt-10 grid grid-cols-3 gap-6 border-y border-[hsl(var(--sk-border))] py-8">
          <div>
            <p className="label-eyebrow text-[hsl(var(--sk-muted-fg))]">{t("shop.roast")}</p>
            <p className="mt-2 capitalize">{p.roast_profile}</p>
          </div>
          <div>
            <p className="label-eyebrow text-[hsl(var(--sk-muted-fg))]">{t("shop.weight")}</p>
            <p className="mt-2">{p.weight_grams}g</p>
          </div>
          <div>
            <p className="label-eyebrow text-[hsl(var(--sk-muted-fg))]">{t("shop.origin")}</p>
            <p className="mt-2">{p.origin}</p>
          </div>
        </div>
        {p.tasting_notes?.length > 0 && (
          <div className="mt-8">
            <p className="label-eyebrow text-[hsl(var(--sk-muted-fg))] mb-3">{t("shop.notes")}</p>
            <div className="flex flex-wrap gap-2">
              {p.tasting_notes.map((n) => (
                <Badge key={n} variant="outline" className="border-[hsl(var(--sk-border))]">{n}</Badge>
              ))}
            </div>
          </div>
        )}
        <div className="mt-10 flex items-center gap-6">
          <p className="font-mono-stat text-3xl">{formatIDR(p.price)}</p>
          <Button size="lg" data-testid="add-to-cart-btn"
            onClick={() => { add(p); toast.success("Added to cart"); }}
            className="rounded-full bg-[hsl(var(--sk-primary))] hover:bg-[hsl(var(--sk-primary))]/90 text-white px-8">
            <PackageIcon size={16} className="mr-2" /> {t("shop.add_to_cart")}
          </Button>
        </div>
      </div>
    </div>
  );
}
